import type { DeclarationItem, FundPolicy } from '../../../core/fundPolicy/schema';
import type { DeclarationAnswer } from '../domain/declaration/schema';

/**
 * Builds and validates the declaration from the *stored* policy.
 *
 * Not one question is written into the application. The committee adds, retires,
 * and reorders items from the settings screen, and this reads whatever is there
 * (spec FR-016). Pure, so it can be tested without rendering.
 */

export type AnswerValue = boolean | string | number | readonly string[];
export type AnswerMap = Readonly<Record<string, AnswerValue>>;

/** Active items in display order. Retired items are never offered. */
export function visibleItems(policy: FundPolicy): readonly DeclarationItem[] {
  return policy.declarationItems
    .filter((item) => item.active)
    .slice()
    .sort((a, b) => a.order - b.order);
}

/** True when an answer counts as given. */
export function isAnswered(item: DeclarationItem, value: AnswerValue | undefined): boolean {
  if (value === undefined) return false;

  switch (item.type) {
    case 'boolean':
      // `false` is a real answer to "do you have a chronic condition?" —
      // treating it as unanswered would make "no" impossible to record.
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && !Number.isNaN(value);
    case 'multiselect':
      return Array.isArray(value) && value.length > 0;
    default:
      return typeof value === 'string' && value.trim() !== '';
  }
}

export interface DeclarationValidation {
  /** Item ids that are required and still unanswered. */
  readonly missingItemIds: readonly string[];
  /** Their labels, so the screen can name them all at once (FR-018). */
  readonly missingLabelsAr: readonly string[];
  readonly valid: boolean;
}

export function validateDeclaration(
  policy: FundPolicy,
  answers: AnswerMap,
): DeclarationValidation {
  const missing = visibleItems(policy).filter(
    (item) => item.required && !isAnswered(item, answers[item.id]),
  );

  return {
    missingItemIds: missing.map((item) => item.id),
    missingLabelsAr: missing.map((item) => item.labelAr),
    valid: missing.length === 0,
  };
}

/**
 * Splits answers into the two documents they are stored in.
 *
 * Sensitive answers live in a separate subcollection so that listing
 * declarations — the committee's most frequent operation — never reads
 * anyone's health information, and finance cannot reach it at all
 * (ADR-004, spec FR-021).
 */
export interface SplitAnswers {
  readonly administrative: readonly DeclarationAnswer[];
  readonly medical: readonly DeclarationAnswer[];
}

export function splitAnswers(policy: FundPolicy, answers: AnswerMap): SplitAnswers {
  const administrative: DeclarationAnswer[] = [];
  const medical: DeclarationAnswer[] = [];

  for (const item of visibleItems(policy)) {
    const value = answers[item.id];
    if (!isAnswered(item, value)) continue;

    // `value` is narrowed by isAnswered; the cast is to the union the schema
    // accepts rather than a widening.
    const answer: DeclarationAnswer = {
      itemId: item.id,
      value: value as DeclarationAnswer['value'],
    };

    if (item.sensitive) medical.push(answer);
    else administrative.push(answer);
  }

  return { administrative, medical };
}

/** Rebuilds the editing map from what was stored. */
export function toAnswerMap(answers: readonly DeclarationAnswer[]): AnswerMap {
  const map: Record<string, AnswerValue> = {};
  for (const answer of answers) {
    map[answer.itemId] = answer.value;
  }
  return map;
}

/**
 * Whether the declaration window is open right now.
 *
 * Checked against the stored window rather than a build-time constant, and
 * re-checked by the security rules — this is a courtesy to the employee, not
 * the enforcement.
 */
export function isWindowOpen(policy: FundPolicy, nowIso: string): boolean {
  return (
    policy.employeeSubmissionsEnabled &&
    nowIso >= policy.declarationWindow.opensAt &&
    nowIso <= policy.declarationWindow.closesAt
  );
}
